import PageHeader from "../../components/layout/PageHeader.jsx";
import Location from "../../hooks/Location";

import { client } from "../../hooks/Client.js";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { Viewer } from "../../components/liveReport/reportBuilder2/editor/Viewer.jsx";
import { getEditorUrl } from "../../components/liveReport/reportBuilder2/utils/editorUrl.js";
import duplicateReport from "../../components/liveReport/reportBuilder2/utils/duplicateReport";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import DeleteModal from "../../components/common/DeleteModal.jsx";
import { LiveReportInfoCard } from "../../components/liveReport/reportBuilder2/components/LiveReportInfoCard";
import ShareReportModal from "../../components/liveReport/reportBuilder2/components/ShareReportModal.jsx";
import { TemplateDataProvider } from "../../components/liveReport/reportBuilder2/context/TemplateDataContext.jsx";
import { LiveReportCrudContext } from "../../components/liveReport/reportBuilder2/context/LiveReportCrudContext.jsx";
import useLiveReportWidgets from "../../hooks/useLiveReportWidgets.js";
import { isSuperAdminTenant } from "../../utils/superAdminTenant.js";
import { ReportPeriodContext } from "../../components/liveReport/reportBuilder2/context/ReportPeriodContext.jsx";
import ReportPersonaContext from "../../StateManagment/ReportPersonaContext";
import reportToPeriodRange from "../../helpers/liveReportHelpers/reportToPeriodRange.js";

const LivereportTemplateViewer = () => {

    const history = useHistory();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);

    const liveReportCrudContext = useContext(LiveReportCrudContext);
    // De globale snackbar; die overleeft het openen van de kopie.
    const [, , , setSavedMessage] = useContext(SavedIcon);

    if (!liveReportCrudContext) {
        throw new Error('Missing LiveReportCrudContext.');
    }

    const {
        report,
        createNewReportFromSource,
        updateReport,
        deleteReport,
    } = liveReportCrudContext;

    // Hooks
    const reportId = Location()[3];
    // The hook decides dual-subscribe from `report.IsTemplate`. Forcing
    // `asCopy: true` here made it dual-subscribe for own non-template
    // reports too, which broke widget loading when reopening from the overview.
    const widgets = useLiveReportWidgets({ reportId, report });

    /* Deletion feedback: the DeleteModal closes as soon as it's confirmed,
       but deleting the report + all its widget docs takes a moment — surface
       that as a spinner in the info card's delete button. */
    const [deletePending, setDeletePending] = useState(false);

    /* Successful deletion navigates away and unmounts this page — guard the
       state reset (React 17 warns on setState after unmount). */
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const handleDelete = async () => {
        if (!report) return;
        setDeletePending(true);
        try {
            await deleteReport(report, () => {
                history.push(`/${client}/livereports2`);
            });
        } finally {
            if (isMounted.current) setDeletePending(false);
        }
    };

    const canManageTemplate = !report?.IsTemplate || isSuperAdminTenant(client);

    const periodRange = useMemo(() => reportToPeriodRange(report), [report]);

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader 
                    heading={report && report.IsTemplate
                        ? 'Live rapportage template'
                        : 'Live rapportage'}
                    subHeading={``}
                    description={''}
                    AI={false}
                />

                {report && (
                    <div className="template-viewer-header">
                        <LiveReportInfoCard
                            editable={false}
                            report={report}
                            onReportNameChange={canManageTemplate ? (newTitle) => {
                                updateReport(report, { Name: newTitle });
                            } : undefined}
                            handleEdit={canManageTemplate ? () => {
                                history.push(getEditorUrl(report.ID, report.IsTemplate ?? false));
                            } : undefined}
                            handleSetStatus={canManageTemplate ? async () => {
                                const newStatus = report.Status === 'published'
                                    ? 'draft'
                                    : 'published';
                                await updateReport(report, { Status: newStatus });
                            } : undefined}
                            handleDuplicate={canManageTemplate ? () => duplicateReport({
                                report,
                                createNewReportFromSource,
                                navigate: history.push,
                                notify: setSavedMessage,
                            }) : undefined}
                            handleNewFromTemplate={async () => {
                                const newReport = await createNewReportFromSource(report, false);
                                history.push(getEditorUrl(newReport.ID, false));
                            }}
                            setDeleteModalOpen={canManageTemplate ? setDeleteModalOpen : undefined}
                            handleShare={() => setShareModalOpen(true)}
                            deletePending={deletePending}
                        />

                        {/* <div className="template-viewer-preview-header">
                            <h3>Voorbeeld</h3>
                            <p>Live weergave van deze rapportage template</p>
                        </div> */}
                    </div>
                )}

                <div className="template-viewer-preview-container">
                    <TemplateDataProvider isTemplate={report?.IsTemplate ?? false}>
                        <ReportPeriodContext.Provider value={periodRange}>
                            <ReportPersonaContext.Provider value={report?.PersonaID ?? null}>
                                {widgets && <Viewer widgets={widgets} />}
                            </ReportPersonaContext.Provider>
                        </ReportPeriodContext.Provider>
                    </TemplateDataProvider>
                </div>

                {/* Gemount bij openen: het venster leest de code en de
                    deelnemer live van het rapport uit de context. */}
                {report && shareModalOpen && (
                    <ShareReportModal
                        modalOpen={true}
                        setModalOpen={setShareModalOpen}
                        report={report}
                    />
                )}

                <DeleteModal 
                    deleteModal={deleteModalOpen} 
                    setDeleteModal={setDeleteModalOpen}
                    deleteItem={handleDelete}
                    deleteName={report?.Name || 'deze rapportage'} 
                    linkedNote="De widgets van deze rapportage gaan mee, en komen mee terug als je de rapportage terugzet."
                    softDelete
                />
            </div> 
        </div>
    )
}

export default LivereportTemplateViewer